import type { EditorClipFit, EditorTimelineClip } from '@/types/editor';

export const MIN_CLIP_RATE = 0.25;
export const MAX_CLIP_RATE = 4;
export const MIN_CLIP_SCALE = 0.25;
export const MAX_CLIP_SCALE = 4;

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

export function clipPlaybackRate(clip: Pick<EditorTimelineClip, 'playbackRate'>): number {
  return clamp(clip.playbackRate, MIN_CLIP_RATE, MAX_CLIP_RATE, 1);
}

export function clipFit(clip: Pick<EditorTimelineClip, 'fit'>): EditorClipFit {
  return clip.fit === 'cover' ? 'cover' : 'contain';
}

export function clipPositionX(clip: Pick<EditorTimelineClip, 'positionX'>): number {
  return clamp(clip.positionX, 0, 100, 50);
}

export function clipPositionY(clip: Pick<EditorTimelineClip, 'positionY'>): number {
  return clamp(clip.positionY, 0, 100, 50);
}

export function clipScale(clip: Pick<EditorTimelineClip, 'scale'>): number {
  return clamp(clip.scale, MIN_CLIP_SCALE, MAX_CLIP_SCALE, 1);
}

export function clipRotation(clip: Pick<EditorTimelineClip, 'rotation'>): number {
  return clamp(clip.rotation, -180, 180, 0);
}

export function clipOpacity(clip: Pick<EditorTimelineClip, 'opacity'>): number {
  return clamp(clip.opacity, 0, 1, 1);
}

/** Source-media seconds consumed by a clip's timeline window. */
export function clipSourceDuration(
  clip: Pick<EditorTimelineClip, 'duration' | 'playbackRate'>
): number {
  return Math.max(0, Number(clip.duration) || 0) * clipPlaybackRate(clip);
}

/** Source-media time for a local timeline time inside the clip. */
export function clipMediaTime(
  clip: Pick<EditorTimelineClip, 'mediaOffset' | 'playbackRate'>,
  localTimelineTime: number
): number {
  return Math.max(0, Number(clip.mediaOffset) || 0) +
    Math.max(0, localTimelineTime) * clipPlaybackRate(clip);
}

/**
 * FFmpeg's `atempo` accepts only 0.5–2.0 per filter. Split wider supported
 * editor rates into a stable chain (e.g. 0.25 → 0.5,0.5; 4 → 2,2).
 */
export function atempoFilters(rate: number): number[] {
  let remaining = clamp(rate, MIN_CLIP_RATE, MAX_CLIP_RATE, 1);
  const filters: number[] = [];
  while (remaining < 0.5 - 1e-6) {
    filters.push(0.5);
    remaining /= 0.5;
  }
  while (remaining > 2 + 1e-6) {
    filters.push(2);
    remaining /= 2;
  }
  if (Math.abs(remaining - 1) > 1e-6 || filters.length === 0) filters.push(remaining);
  return filters;
}

export function hasCustomClipTransform(
  clip: Pick<EditorTimelineClip, 'positionX' | 'positionY' | 'scale' | 'rotation' | 'opacity'>
): boolean {
  return clipPositionX(clip) !== 50 || clipPositionY(clip) !== 50 || clipScale(clip) !== 1 ||
    clipRotation(clip) !== 0 || clipOpacity(clip) !== 1;
}
